import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PLAYER_COLORS, BASE_SIZE } from '../../../utils/constants.js';
import { matte, colored, vary, seededRandom } from '../../Style.js';
import { SEA, NEUTRAL, hashId, buoyParts, ropeParts, foamRing, foamMesh } from './style.js';

const DIMMED_COLOR = '#6b6f75';
const DECK = 0.35; // Dock deck height above the zone's base

// A shipyard berth: a square timber dock round a patch of open water, team
// bunting strung between its corner posts and buoys round it, with a little
// model of the vessel for sale bobbing in the middle. Forward-base claim zones
// are a wide ring of flagged marker buoys.
export class BuyZone {
  constructor(scene, zone) {
    this.scene = scene;
    this.zone = zone;
    this.mesh = null; // Root group, positioned at the zone centre
    this.label = null;
    this.glowMesh = null;
    this.inlayMesh = null;
    this.iconMesh = null;
    this.iconMaterials = [];
    this.teamMaterials = [];
    this.canAfford = true;
    this.baseColor = this.getZoneColor(zone.ownerId);
    this.rand = seededRandom(hashId(zone.id));
    this.phase = this.rand() * Math.PI * 2;
    this.create();
  }

  getZoneColor(ownerId) {
    if (ownerId === -1) {
      return NEUTRAL;
    }
    return PLAYER_COLORS[ownerId];
  }

  create() {
    this.mesh = new THREE.Group();
    // A hair of per-zone lift so overlapping docks don't z-fight
    const baseY = (this.zone.position.y || 0) + this.rand() * 0.03;
    this.mesh.position.set(this.zone.position.x, baseY, this.zone.position.z);

    if (this.zone.unitType) {
      this.createDock(baseY);
    } else {
      this.createClaimRing(baseY);
    }

    this.scene.add(this.mesh);
  }

  // Lit material that follows the owner's colour
  teamMaterial(opts = {}) {
    const material = new THREE.MeshStandardMaterial({
      color: this.baseColor,
      emissive: this.baseColor,
      emissiveIntensity: opts.emissive ?? 0.2,
      roughness: 0.7,
      flatShading: true,
      transparent: true,
      side: opts.side ?? THREE.FrontSide
    });
    this.teamMaterials.push(material);
    return material;
  }

  createDock(baseY) {
    // In a base harbour the berth is a pier running out from the back strand
    const { x, z } = this.zone.position;
    const inHarbour = baseY < 0.5 && Math.abs(Math.abs(x) - 90) <= BASE_SIZE / 2 && Math.abs(z) <= (BASE_SIZE * 1.15) / 2;
    if (inHarbour) {
      this.createPier();
      return;
    }

    const rand = this.rand;
    const half = Math.min(this.zone.radius - 1.2, 2.8);
    const opening = 1.55;
    const width = half - opening;
    const parts = [];
    const trim = [];
    // Docks up on an island have no sea beneath them
    const dry = baseY > 0.5;

    // Four plank decks framing the berth, on pilings
    const sides = [[0, 1], [0, -1], [1, 0], [-1, 0]];
    for (const [sx, sz] of sides) {
      const alongX = sz !== 0;
      const length = alongX ? half * 2 : opening * 2;
      const cx = sx * (opening + width / 2);
      const cz = sz * (opening + width / 2);
      const planks = Math.max(3, Math.round(width / 0.35));
      for (let i = 0; i < planks; i++) {
        const o = -width / 2 + (width / planks) * (i + 0.5);
        parts.push(colored(new THREE.BoxGeometry(alongX ? length : width / planks - 0.04, 0.1, alongX ? width / planks - 0.04 : length), vary(SEA.woodLight, rand, 0.12), {
          x: cx + (alongX ? 0 : o), y: DECK - 0.05, z: cz + (alongX ? o : 0)
        }));
      }
      // Team-painted edge round the berth
      trim.push(colored(new THREE.BoxGeometry(alongX ? opening * 2 + 0.2 : 0.16, 0.14, alongX ? 0.16 : opening * 2 + 0.2), 0xffffff, {
        x: sx * (opening + 0.08), y: DECK + 0.02, z: sz * (opening + 0.08)
      }));
      // Bearers under the planks
      parts.push(colored(new THREE.BoxGeometry(alongX ? length : 0.18, 0.18, alongX ? 0.18 : length), SEA.woodDark, { x: cx, y: DECK - 0.19, z: cz }));
    }
    // Pilings and tall corner posts
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        for (const [px, pz] of [[sx * half, sz * half], [sx * opening, sz * half], [sx * half, sz * opening]]) {
          parts.push(colored(new THREE.CylinderGeometry(0.12, 0.14, DECK + 0.9, 6), vary(SEA.woodDark, rand, 0.1), { x: px, y: (DECK + 0.9) / 2 - 0.9, z: pz }));
        }
        parts.push(colored(new THREE.CylinderGeometry(0.13, 0.15, 1.9, 6), SEA.woodDark, { x: sx * half, y: DECK + 0.95 - 0.2, z: sz * half }));
        trim.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.2, 6), 0xffffff, { x: sx * half, y: DECK + 1.7, z: sz * half }));
        // Mooring cleats at the berth's corners
        parts.push(colored(new THREE.BoxGeometry(0.32, 0.1, 0.1), SEA.iron, { x: sx * (opening + 0.35), y: DECK + 0.05, z: sz * (opening + 0.35) }));
      }
    }

    // Rope round the corner posts, with bunting hanging from it
    const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sz]) => new THREE.Vector3(sx * half, DECK + 1.6, sz * half));
    const teamFlags = [];
    const whiteFlags = [];
    for (let i = 0; i < 4; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % 4];
      parts.push(...ropeParts(a, b, { sag: 0.35, radius: 0.025, segments: 4 }));
      const count = 5;
      for (let k = 0; k < count; k++) {
        const t = (k + 0.5) / count;
        const p = a.clone().lerp(b, t);
        p.y -= Math.sin(t * Math.PI) * 0.35;
        const flag = new THREE.ConeGeometry(0.18, 0.4, 3);
        flag.rotateX(Math.PI);
        flag.scale(1, 1, 0.2);
        flag.rotateY(Math.atan2(b.x - a.x, b.z - a.z) + Math.PI / 2);
        (k % 2 ? whiteFlags : teamFlags).push(colored(flag, 0xffffff, { x: p.x, y: p.y - 0.2, z: p.z }));
      }
    }
    parts.push(...whiteFlags.map(g => colored(g, SEA.white)));

    // Little marker buoys bobbing round the dock, when it stands in the water
    const buoyTeam = [];
    const buoys = dry ? 0 : 6;
    const ring = this.zone.radius - 0.4;
    for (let i = 0; i < buoys; i++) {
      const a = ((i + 0.5) / buoys) * Math.PI * 2;
      const pieces = buoyParts(0xffffff, { x: Math.cos(a) * ring, y: -0.05, z: Math.sin(a) * ring, height: 0.6 });
      pieces.forEach((piece, k) => (k % 2 ? parts.push(colored(piece, SEA.white)) : buoyTeam.push(piece)));
    }

    // On dry land the berth is a stone-lined basin of water
    if (dry) {
      parts.push(colored(new THREE.BoxGeometry(opening * 2, 0.3, opening * 2), SEA.stone, { y: 0.15 }));
      parts.push(colored(new THREE.BoxGeometry(opening * 2 - 0.3, 0.04, opening * 2 - 0.3), 0x2fb4c2, { y: 0.29 }));
    } else {
      this.mesh.add(foamMesh(foamRing(opening * 1.1, 0.5, 24), 0.04 - baseY));
    }

    const dock = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    dock.castShadow = true;
    dock.receiveShadow = true;
    this.mesh.add(dock);

    this.glowMesh = new THREE.Mesh(mergeGeometries([...trim, ...teamFlags, ...buoyTeam]), this.teamMaterial({ emissive: 0.3, side: THREE.DoubleSide }));
    this.glowMesh.castShadow = true;
    this.mesh.add(this.glowMesh);

    this.createUnitIcon(dry ? 0.3 : 0);
  }

  // A U-shaped berth round the model, joined to the base's back strand by a
  // walkway, with team bunting on its posts and buoys off its open end.
  createPier() {
    const rand = this.rand;
    const zx = this.zone.position.x;
    // Local frame: s runs from the back wall towards the arena centre, t is z
    const front = zx < 0 ? 1 : -1;
    const L = (s, t, y = 0) => new THREE.Vector3(front * s, y, t);
    // The base strand's harbour edge, 3.5m in from the wall at x = ±100
    const strand = Math.abs(zx) - 96.5;
    const open = 1.45;
    const arm = 0.95;
    const backS = -open - arm;
    const tipS = 1.9;
    const parts = [];
    const trim = [];

    // Planked deck from s0 to s1 between t0 and t1, boards running across s
    const deck = (s0, s1, t0, t1) => {
      const boards = Math.max(2, Math.round((s1 - s0) / 0.32));
      for (let i = 0; i < boards; i++) {
        const s = s0 + ((s1 - s0) / boards) * (i + 0.5);
        const p = L(s, (t0 + t1) / 2, DECK - 0.05);
        parts.push(colored(new THREE.BoxGeometry((s1 - s0) / boards - 0.04, 0.1, t1 - t0), vary(SEA.woodLight, rand, 0.12), { x: p.x, y: p.y, z: p.z }));
      }
      for (const t of [t0 + 0.15, t1 - 0.15]) {
        const p = L((s0 + s1) / 2, t, DECK - 0.19);
        parts.push(colored(new THREE.BoxGeometry(s1 - s0, 0.18, 0.18), SEA.woodDark, { x: p.x, y: p.y, z: p.z }));
      }
      for (let s = s0; s <= s1 + 0.01; s += Math.max(1.2, (s1 - s0) / Math.ceil((s1 - s0) / 1.8))) {
        for (const t of [t0 + 0.1, t1 - 0.1]) {
          const p = L(s, t);
          parts.push(colored(new THREE.CylinderGeometry(0.12, 0.14, DECK + 0.9, 6), vary(SEA.woodDark, rand, 0.1), { x: p.x, y: (DECK + 0.9) / 2 - 0.9, z: p.z }));
        }
      }
    };

    // The U round the berth: a back deck and two arms reaching to the open end
    deck(backS, -open, -open - arm, open + arm);
    deck(-open, tipS, open, open + arm);
    deck(-open, tipS, -open - arm, -open);
    // Walkway back to the strand, when the berth stands clear of it
    if (strand - 0.6 < backS - 0.3) deck(strand - 0.6, backS, -0.8, 0.8);

    // Team-painted edge round the inside of the berth
    trim.push(colored(new THREE.BoxGeometry(0.16, 0.14, open * 2 + 0.3), 0xffffff, { x: L(-open - 0.08, 0).x, y: DECK + 0.02, z: 0 }));
    for (const t of [-1, 1]) {
      const p = L((tipS - open) / 2, t * (open + 0.08), DECK + 0.02);
      trim.push(colored(new THREE.BoxGeometry(tipS + open, 0.14, 0.16), 0xffffff, { x: p.x, y: p.y, z: p.z }));
    }

    // Tall posts at the corners carrying rope and bunting round three sides
    const outer = open + arm - 0.12;
    const corners = [L(tipS - 0.1, -outer, DECK + 1.6), L(backS + 0.1, -outer, DECK + 1.6), L(backS + 0.1, outer, DECK + 1.6), L(tipS - 0.1, outer, DECK + 1.6)];
    for (const c of corners) {
      parts.push(colored(new THREE.CylinderGeometry(0.13, 0.15, 1.9, 6), SEA.woodDark, { x: c.x, y: DECK + 0.75, z: c.z }));
      trim.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.2, 6), 0xffffff, { x: c.x, y: DECK + 1.7, z: c.z }));
    }
    for (let i = 0; i < corners.length - 1; i++) {
      const a = corners[i];
      const b = corners[i + 1];
      parts.push(...ropeParts(a, b, { sag: 0.35, radius: 0.025, segments: 4 }));
      const count = Math.max(3, Math.round(a.distanceTo(b) / 0.7));
      for (let k = 0; k < count; k++) {
        const t = (k + 0.5) / count;
        const p = a.clone().lerp(b, t);
        p.y -= Math.sin(t * Math.PI) * 0.35;
        const flag = new THREE.ConeGeometry(0.18, 0.4, 3);
        flag.rotateX(Math.PI);
        flag.scale(1, 1, 0.2);
        flag.rotateY(Math.atan2(b.x - a.x, b.z - a.z) + Math.PI / 2);
        const geo = colored(flag, 0xffffff, { x: p.x, y: p.y - 0.2, z: p.z });
        if (k % 2) parts.push(colored(geo, SEA.white));
        else trim.push(geo);
      }
    }
    // Mooring cleats inside the berth
    for (const t of [-1, 1]) {
      const p = L(0.4, t * (open + 0.3), DECK + 0.05);
      parts.push(colored(new THREE.BoxGeometry(0.1, 0.1, 0.32), SEA.iron, { x: p.x, y: p.y, z: p.z }));
    }

    // Marker buoys off the open end
    const buoyTeam = [];
    for (const t of [-1, 1]) {
      const p = L(tipS + 0.9, t * outer);
      buoyParts(0xffffff, { x: p.x, y: -0.05, z: p.z, height: 0.7 }).forEach((piece, k) => (k % 2 ? parts.push(colored(piece, SEA.white)) : buoyTeam.push(piece)));
    }

    const dock = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    dock.castShadow = true;
    dock.receiveShadow = true;
    this.mesh.add(dock);

    this.glowMesh = new THREE.Mesh(mergeGeometries([...trim, ...buoyTeam]), this.teamMaterial({ emissive: 0.3, side: THREE.DoubleSide }));
    this.glowMesh.castShadow = true;
    this.mesh.add(this.glowMesh);

    this.mesh.add(foamMesh(foamRing(open * 0.9, 0.5, 24), 0.04));
    this.createUnitIcon(0);
  }

  createClaimRing(baseY) {
    const radius = this.zone.radius - 0.6;
    const count = 20;
    // Marker spars reach down to the water even when the zone sits on an island
    const groundY = -baseY;
    const top = 2.4;
    const parts = [];
    const team = [];
    let prev = null;
    let first = null;
    for (let i = 0; i <= count; i++) {
      const a = (i / count) * Math.PI * 2;
      const x = Math.cos(a) * radius;
      const z = Math.sin(a) * radius;
      const ropeTop = new THREE.Vector3(x, top - 0.5, z);
      if (i < count) {
        // A float at the waterline and a tall striped spar with a flag
        team.push(colored(new THREE.CylinderGeometry(0.5, 0.38, 0.55, 8), 0xffffff, { x, y: groundY + 0.15, z }));
        parts.push(colored(new THREE.CylinderGeometry(0.52, 0.52, 0.12, 8), SEA.white, { x, y: groundY + 0.38, z }));
        const spar = top - groundY;
        parts.push(colored(new THREE.CylinderGeometry(0.09, 0.13, spar, 6), SEA.white, { x, y: groundY + spar / 2, z }));
        team.push(colored(new THREE.CylinderGeometry(0.14, 0.14, 0.45, 6), 0xffffff, { x, y: top - 0.9, z }));
        team.push(colored(new THREE.ConeGeometry(0.18, 0.3, 6), 0xffffff, { x, y: top + 0.15, z }));
        if (i % 2 === 0) {
          const flag = new THREE.PlaneGeometry(0.9, 0.55).translate(0.45, 0, 0);
          team.push(colored(flag, 0xffffff, { x, y: top - 0.15, z, ry: -a + Math.PI / 2 }));
        }
      }
      if (prev) parts.push(...ropeParts(prev, ropeTop, { sag: 0.3, radius: 0.04, segments: 3 }));
      prev = ropeTop;
      if (!first) first = ropeTop;
    }
    const posts = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    posts.castShadow = true;
    posts.receiveShadow = true;
    this.mesh.add(posts);

    this.glowMesh = new THREE.Mesh(mergeGeometries(team), this.teamMaterial({ emissive: 0.35, side: THREE.DoubleSide }));
    this.glowMesh.castShadow = true;
    this.mesh.add(this.glowMesh);
  }

  createUnitIcon(baseY) {
    const unitType = this.zone.unitType;
    const builders = {
      tank: gunboat,
      super_tank: ironclad,
      airplane: seaplane,
      super_helicopter: airship,
      sniper: harpoonSkiff,
      rocket_launcher: mortarBarge
    };
    const build = builders[unitType];
    if (!build) return;

    const { parts, accent, lift } = build();
    const icon = new THREE.Group();
    const body = matte(0xffffff, { vertexColors: true, unique: true });
    body.transparent = true;
    body.userData.baseOpacity = 1;
    const model = new THREE.Mesh(mergeGeometries(parts), body);
    model.castShadow = true;
    icon.add(model);
    this.iconMaterials.push(body);
    if (accent.length) {
      const paint = this.teamMaterial({ emissive: 0.15 });
      paint.userData.baseOpacity = 1;
      const trim = new THREE.Mesh(mergeGeometries(accent), paint);
      trim.castShadow = true;
      icon.add(trim);
      this.iconMaterials.push(paint);
    }
    icon.scale.setScalar(unitType.startsWith('super_') ? 1.05 : 0.95);
    icon.position.y = baseY + (lift || 0);
    icon.userData.baseY = icon.position.y;
    icon.userData.flying = !!lift;
    this.mesh.add(icon);
    this.iconMesh = icon;
  }

  // Update for animation (bobbing model, breathing trim)
  update(time) {
    if (this.glowMesh && this.zone.unitType) {
      this.glowMesh.material.opacity = this.canAfford ? 0.85 + Math.sin(time * 3 + this.phase) * 0.15 : 0.35;
    }

    if (this.iconMesh) {
      const t = time + this.phase;
      // Boats ride the swell; aircraft hover and turn
      if (this.iconMesh.userData.flying) {
        this.iconMesh.rotation.y = time * 0.6 + this.phase;
        this.iconMesh.position.y = this.iconMesh.userData.baseY + Math.sin(t * 1.4) * 0.15;
      } else {
        this.iconMesh.rotation.y = time * 0.25 + this.phase;
        this.iconMesh.position.y = this.iconMesh.userData.baseY + Math.sin(t * 1.6) * 0.05;
        this.iconMesh.rotation.x = Math.sin(t * 1.3) * 0.05;
        this.iconMesh.rotation.z = Math.cos(t * 1.1) * 0.06;
      }
    }
  }

  // Set whether the player can afford this zone's unit
  setAffordable(canAfford) {
    if (this.canAfford === canAfford) return;

    this.canAfford = canAfford;
    for (const material of this.iconMaterials) {
      material.opacity = material.userData.baseOpacity * (canAfford ? 1.0 : 0.3);
      material.depthWrite = canAfford;
    }
    const color = canAfford ? this.baseColor : DIMMED_COLOR;
    for (const material of this.teamMaterials) {
      material.color.set(color);
      material.emissive.set(color);
    }
  }

  // Check if a position is within this buy zone
  isInRange(position) {
    const dx = position.x - this.zone.position.x;
    const dz = position.z - this.zone.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.zone.radius * this.zone.radius;
  }

  // Update the owner of this zone (when claimed)
  updateOwner(newOwnerId) {
    if (this.zone.ownerId === newOwnerId) return;

    this.zone.ownerId = newOwnerId;
    this.baseColor = this.getZoneColor(newOwnerId);
    if (!this.canAfford) return;
    for (const material of this.teamMaterials) {
      material.color.set(this.baseColor);
      material.emissive.set(this.baseColor);
    }
  }

  remove() {
    if (!this.mesh) return;
    this.scene.remove(this.mesh);
    this.mesh.traverse(child => {
      if (child.isMesh) child.geometry.dispose();
    });
    this.teamMaterials.forEach(material => material.dispose());
    this.iconMaterials.forEach(material => material.dispose());
  }
}

// --- Vessel models, built facing +Z, waterline at y = 0. ---

// A hull: a box with a pointed bow, its deck at `height`, spanning
// z from -length/2 (stern) to +length/2 (bow)
function hull(length, width, height, color, { draft = 0.15 } = {}) {
  const body = length * 0.72;
  const bow = length - body;
  const y = (height - draft) / 2;
  const nose = new THREE.ConeGeometry(width / Math.SQRT2, bow, 4);
  nose.rotateY(Math.PI / 4);
  nose.rotateX(Math.PI / 2);
  nose.scale(1, (height + draft) / width, 1);
  return [
    colored(new THREE.BoxGeometry(width, height + draft, body), color, { y, z: -bow / 2 }),
    colored(nose, color, { y, z: body / 2 })
  ];
}

// Gunboat: a small grey hull, wheelhouse and a deck gun
function gunboat() {
  const parts = hull(2.4, 0.8, 0.35, SEA.hull);
  parts.push(colored(new THREE.BoxGeometry(0.82, 0.04, 1.7), SEA.woodLight, { y: 0.36, z: -0.3 }));
  parts.push(colored(new THREE.BoxGeometry(0.55, 0.4, 0.55), SEA.white, { y: 0.58, z: -0.45 }));
  parts.push(colored(new THREE.BoxGeometry(0.6, 0.06, 0.6), SEA.hull, { y: 0.8, z: -0.45 }));
  parts.push(colored(new THREE.CylinderGeometry(0.18, 0.2, 0.2, 8), SEA.iron, { y: 0.48, z: 0.45 }));
  parts.push(colored(new THREE.CylinderGeometry(0.04, 0.04, 0.6, 6), SEA.iron, { rx: Math.PI / 2, y: 0.52, z: 0.8 }));
  parts.push(colored(new THREE.CylinderGeometry(0.02, 0.02, 0.6, 4), SEA.iron, { y: 1.05, z: -0.45 }));
  const accent = [
    colored(new THREE.BoxGeometry(0.84, 0.08, 1.75), 0xffffff, { y: 0.22, z: -0.3 }),
    colored(new THREE.PlaneGeometry(0.3, 0.18).translate(0.15, 0, 0), 0xffffff, { y: 1.25, z: -0.45, ry: Math.PI / 2 })
  ];
  return { parts, accent };
}

// Ironclad: a low armoured hull with a casemate, twin funnels and a heavy gun
function ironclad() {
  const parts = hull(2.8, 1.05, 0.3, 0x2f343a);
  parts.push(colored(new THREE.BoxGeometry(0.85, 0.35, 1.1), 0x4a5058, { y: 0.47, z: -0.2 }));
  parts.push(colored(new THREE.BoxGeometry(0.7, 0.05, 0.95), 0x2f343a, { y: 0.67, z: -0.2 }));
  for (const z of [-0.45, -0.05]) {
    parts.push(colored(new THREE.CylinderGeometry(0.1, 0.12, 0.55, 8), 0x2a2a2a, { y: 0.9, z }));
  }
  parts.push(colored(new THREE.CylinderGeometry(0.24, 0.26, 0.22, 10), 0x4a5058, { y: 0.42, z: 0.75 }));
  parts.push(colored(new THREE.CylinderGeometry(0.07, 0.08, 0.8, 8), SEA.iron, { rx: Math.PI / 2, y: 0.46, z: 1.15 }));
  const accent = [
    colored(new THREE.BoxGeometry(1.07, 0.07, 2.05), 0xffffff, { y: 0.18, z: -0.35 }),
    colored(new THREE.CylinderGeometry(0.125, 0.125, 0.1, 8), 0xffffff, { y: 1.05, z: -0.45 }),
    colored(new THREE.CylinderGeometry(0.125, 0.125, 0.1, 8), 0xffffff, { y: 1.05, z: -0.05 })
  ];
  return { parts, accent };
}

// Seaplane: high-wing monoplane on twin floats
function seaplane() {
  const parts = [
    colored(new THREE.CylinderGeometry(0.16, 0.08, 1.8, 8), SEA.white, { rx: Math.PI / 2, y: 0.75 }),
    colored(new THREE.SphereGeometry(0.18, 8, 6), SEA.white, { y: 0.75, z: 0.9 }),
    colored(new THREE.BoxGeometry(0.06, 0.08, 0.5), SEA.iron, { y: 0.75, z: 1.08 }),
    colored(new THREE.BoxGeometry(0.5, 0.05, 0.08), SEA.iron, { y: 0.75, z: 1.1 }),
    colored(new THREE.BoxGeometry(0.7, 0.04, 0.3), SEA.white, { y: 0.8, z: -0.8 }),
    colored(new THREE.BoxGeometry(0.15, 0.08, 0.15), 0x88c8e8, { y: 0.9, z: 0.45 })
  ];
  for (const x of [-0.35, 0.35]) {
    parts.push(colored(new THREE.CylinderGeometry(0.08, 0.08, 1.2, 6), SEA.white, { rx: Math.PI / 2, x, y: 0.1, z: 0.1 }));
    parts.push(colored(new THREE.BoxGeometry(0.04, 0.6, 0.04), SEA.iron, { x, y: 0.42, z: 0.35, rz: x > 0 ? -0.3 : 0.3 }));
  }
  const accent = [
    colored(new THREE.BoxGeometry(2.4, 0.06, 0.45), 0xffffff, { y: 1.0, z: 0.35 }),
    colored(new THREE.BoxGeometry(0.04, 0.35, 0.3), 0xffffff, { y: 0.95, z: -0.85 })
  ];
  return { parts, accent, lift: 0.5 };
}

// Airship: a long envelope with fins and a slung gondola
function airship() {
  const parts = [
    colored(new THREE.SphereGeometry(0.55, 12, 8), 0xd8d2c0, { y: 1.5, scale: [1, 1, 2.3] }),
    colored(new THREE.BoxGeometry(0.35, 0.25, 0.8), SEA.woodDark, { y: 0.78 }),
    colored(new THREE.BoxGeometry(0.02, 0.4, 0.02), SEA.iron, { x: 0.12, y: 1.0, z: 0.3 }),
    colored(new THREE.BoxGeometry(0.02, 0.4, 0.02), SEA.iron, { x: -0.12, y: 1.0, z: -0.3 }),
    colored(new THREE.CylinderGeometry(0.06, 0.06, 0.15, 6), SEA.iron, { rx: Math.PI / 2, y: 0.78, z: -0.5 })
  ];
  const accent = [
    colored(new THREE.CylinderGeometry(0.56, 0.56, 0.16, 12), 0xffffff, { rx: Math.PI / 2, y: 1.5, z: 0.2 }),
    colored(new THREE.BoxGeometry(0.04, 0.6, 0.45), 0xffffff, { y: 1.75, z: -1.05 }),
    colored(new THREE.BoxGeometry(0.04, 0.6, 0.45), 0xffffff, { y: 1.25, z: -1.05 }),
    colored(new THREE.BoxGeometry(0.6, 0.04, 0.45), 0xffffff, { x: 0.25, y: 1.5, z: -1.05 }),
    colored(new THREE.BoxGeometry(0.6, 0.04, 0.45), 0xffffff, { x: -0.25, y: 1.5, z: -1.05 })
  ];
  return { parts, accent, lift: 0.4 };
}

// Harpoon Skiff: a slim open boat with a harpoon gun on the bow
function harpoonSkiff() {
  const parts = hull(2.2, 0.6, 0.25, SEA.wood);
  parts.push(colored(new THREE.BoxGeometry(0.5, 0.04, 1.4), SEA.woodLight, { y: 0.2, z: -0.3 }));
  for (const z of [-0.6, -0.1]) parts.push(colored(new THREE.BoxGeometry(0.56, 0.05, 0.12), SEA.woodDark, { y: 0.3, z }));
  parts.push(colored(new THREE.CylinderGeometry(0.05, 0.07, 0.3, 6), SEA.iron, { y: 0.42, z: 0.55 }));
  parts.push(colored(new THREE.CylinderGeometry(0.035, 0.035, 0.9, 6), SEA.iron, { rx: Math.PI / 2 - 0.15, y: 0.58, z: 0.85 }));
  parts.push(colored(new THREE.ConeGeometry(0.06, 0.2, 5), 0xc0c4c8, { rx: Math.PI / 2 - 0.15, y: 0.64, z: 1.35 }));
  parts.push(colored(new THREE.TorusGeometry(0.1, 0.03, 4, 10), SEA.rope, { rx: Math.PI / 2, y: 0.3, z: 0.3 }));
  const accent = [colored(new THREE.BoxGeometry(0.62, 0.07, 1.62), 0xffffff, { y: 0.15, z: -0.3 })];
  return { parts, accent };
}

// Mortar Barge: a flat square barge carrying a squat mortar and shells
function mortarBarge() {
  const parts = [
    colored(new THREE.BoxGeometry(1.3, 0.35, 2.0), SEA.woodDark, { y: 0.1 }),
    colored(new THREE.BoxGeometry(1.34, 0.05, 2.04), SEA.woodLight, { y: 0.3 }),
    colored(new THREE.CylinderGeometry(0.36, 0.42, 0.2, 10), SEA.iron, { y: 0.42 }),
    colored(new THREE.CylinderGeometry(0.26, 0.3, 0.6, 10), 0x2c2c30, { y: 0.75, z: 0.12, rx: 0.4 }),
    colored(new THREE.TorusGeometry(0.27, 0.05, 4, 10), SEA.iron, { rx: Math.PI / 2 + 0.4, y: 1.03, z: 0.24 })
  ];
  for (let i = 0; i < 3; i++) parts.push(colored(new THREE.SphereGeometry(0.1, 6, 4), 0x2c2c30, { x: -0.4 + i * 0.12, y: 0.4, z: -0.7 }));
  parts.push(colored(new THREE.BoxGeometry(0.4, 0.3, 0.3), SEA.woodLight, { x: 0.35, y: 0.45, z: -0.7 }));
  const accent = [colored(new THREE.BoxGeometry(1.36, 0.08, 2.06), 0xffffff, { y: 0.17 })];
  return { parts, accent };
}
